
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CalendarDays, Clock } from 'lucide-react';

interface TimeSlot {
  time: string;
  fullDate: Date;
}

interface VehicleCollectionTimeSelectorProps {
  selectedDate: Date | undefined;
  availableTimesForDate: (date: Date) => TimeSlot[];
  onTimeSelect: (fullDate: Date) => void;
}

const VehicleCollectionTimeSelector: React.FC<VehicleCollectionTimeSelectorProps> = ({
  selectedDate,
  availableTimesForDate,
  onTimeSelect
}) => {
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Clock className="w-5 h-5 text-honeycomb" />
          Available Times
        </CardTitle>
      </CardHeader>
      <CardContent className="min-h-[300px]">
        {selectedDate ? (
          availableTimesForDate(selectedDate).length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {availableTimesForDate(selectedDate).map((timeSlot, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant={timeSlot.fullDate.getTime() === selectedDate?.getTime() ? "default" : "outline"}
                    className={cn(
                      "w-full justify-start h-12 text-left",
                      timeSlot.fullDate.getTime() === selectedDate?.getTime() 
                        ? "bg-honeycomb hover:bg-amber-500 text-white" 
                        : "hover:bg-gray-50"
                    )}
                    onClick={() => onTimeSelect(timeSlot.fullDate)}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {timeSlot.time}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Clock className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500">No times available for this date</p>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <CalendarDays className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-500">Please select a date first</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VehicleCollectionTimeSelector;
